import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    DeleteProduct as DeleteProductMutation,
    DeleteProductVariables as DeleteProductVariables,
    ProductDeleteInput,
} from 'nutshell-graphql-types';

import {bustManagedCollectionCache} from '../../../../../util/bust-managed-collection-cache';
import GET_PRODUCT_CATEGORIES from '../../../../categories/graphql/queries/get-product-categories.graphql';
import GET_PRODUCTS_SIDEBAR_COUNTS from '../../../../categories/graphql/queries/get-products-sidebar-counts.graphql';

import DELETE_PRODUCT from '../mutations/delete-product.graphql';
import GET_PRODUCTS from '../queries/get-products.graphql';

import {GET_PRODUCTS_LIMIT} from './use-get-products';

export const useDeleteProduct = (
    selectedCategoryId?: string | null,
    archived?: boolean | null
): {
    deleteProduct: (arg1: ProductDeleteInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [deleteProduct, {loading, error}] = useMutation<
        DeleteProductMutation,
        DeleteProductVariables
    >(DELETE_PRODUCT);

    return {
        deleteProduct: (input: ProductDeleteInput) =>
            deleteProduct({
                variables: {input},
                refetchQueries: [
                    {query: GET_PRODUCTS_SIDEBAR_COUNTS},
                    {query: GET_PRODUCT_CATEGORIES},
                ],
                update: (proxy, {data}) => {
                    const product = data && data.productDelete && data.productDelete.product;

                    if (!product) return;

                    const products = proxy.readQuery({
                        query: GET_PRODUCTS,
                        variables: {
                            first: GET_PRODUCTS_LIMIT,
                            categoryId: selectedCategoryId,
                            archived,
                        },
                    });

                    if (!products) return;

                    // @ts-expect-error
                    const newEdges = products.products.edges.filter(
                        // @ts-expect-error
                        (edge) => edge.node.id !== product.id
                    );

                    proxy.writeQuery({
                        query: GET_PRODUCTS,
                        variables: {
                            first: GET_PRODUCTS_LIMIT,
                            categoryId: selectedCategoryId,
                            archived,
                        },
                        data: {
                            products: {
                                edges: newEdges,
                                __typename: 'ProductsConnection',
                            },
                            __typename: 'Query',
                        },
                    });
                },
            }).then(() => {
                // Ensure REST-backed products lists refresh to include product changes
                bustManagedCollectionCache('products');
                bustManagedCollectionCache('productCategories');
            }),
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
